import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useCorrespondentsCache } from '../../hooks/useCorrespondentsCache'
import { useDocumentTypesCache } from '../../hooks/useDocumentTypesCache'
import { useStoragePathsCache } from '../../hooks/useStoragePathsCache'
import { useWorkflowStatusesCache } from '../../hooks/useWorkflowStatusesCache'
import { customFieldService } from '../../services/custom-field.service'
import { DisplayFieldEnum, migrateDisplayFields } from '../../services/document-list-view.service'
import { documentService } from '../../services/document.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { userService } from '../../services/user.service'
import { CustomField, DisplayMode, Document, SavedView, Tag as TagType } from '../../types'
import { formatCustomFieldDisplayValue } from '../../utils/customFieldDisplay'
import { formatDate } from '../../utils/dateFormatter'
import { getDocumentOwnerDisplayName } from '../../utils/documentOwnerDisplay'
import PreviewPopup, { PreviewPopupRef } from '../common/PreviewPopup'
import Tag from '../common/Tag'
import DocumentCardLarge from '../DocumentList/DocumentCardLarge'
import DocumentCardSmall, { type DocumentCardUsersCacheEntry } from '../DocumentList/DocumentCardSmall'
import { DocumentHierarchyView } from '../DocumentList/DocumentHierarchyView'
import './SavedViewWidget.scss'
import { WidgetFrame } from './WidgetFrame'

interface SavedViewWidgetProps {
  savedView: SavedView
}

const DEFAULT_DASHBOARD_VIEW_PAGE_SIZE = 5
const DEFAULT_DASHBOARD_DISPLAY_FIELDS = [DisplayFieldEnum.CORRESPONDENT, DisplayFieldEnum.TITLE, DisplayFieldEnum.TAGS, DisplayFieldEnum.CREATED]

function resolveDisplayFields(fields: (string | { id: string })[] | undefined): DisplayFieldEnum[] {
  if (!fields || fields.length === 0) {
    return DEFAULT_DASHBOARD_DISPLAY_FIELDS
  }

  const stringFields = fields.map((field) => {
    if (typeof field === 'string') return field
    if (typeof field === 'object' && field && 'id' in field) return field.id
    return String(field)
  })

  return migrateDisplayFields(stringFields)
}

function buildSavedViewFetchConfig(savedView: SavedView) {
  return {
    page_size: savedView.page_size || DEFAULT_DASHBOARD_VIEW_PAGE_SIZE,
    sort_field: savedView.sort_field,
    sort_reverse: savedView.sort_reverse,
    filter_rules: savedView.filter_rules,
  }
}

export const SavedViewWidget: React.FC<SavedViewWidgetProps> = ({ savedView }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [documents, setDocuments] = useState<Document[]>([])
  const [count, setCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [show, setShow] = useState<boolean>(false)
  const previewRefs = React.useRef<Map<number, PreviewPopupRef>>(new Map())
  const [usersCache, setUsersCache] = useState<DocumentCardUsersCacheEntry[]>([])
  const [usersCacheLoaded, setUsersCacheLoaded] = useState(false)
  const [customFields, setCustomFields] = useState<CustomField[]>([])

  const { correspondents: correspondentsCache, loaded: correspondentsCacheLoaded } = useCorrespondentsCache()
  const { documentTypes: documentTypesCache, loaded: documentTypesCacheLoaded } = useDocumentTypesCache()
  const { storagePaths: storagePathsCache, loaded: storagePathsCacheLoaded } = useStoragePathsCache()
  const { workflowStatuses: workflowStatusesCache, loaded: workflowStatusesCacheLoaded } = useWorkflowStatusesCache()

  // Drag & Drop
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: savedView.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const displayMode = savedView.display_mode || DisplayMode.SMALL_CARDS
  const displayFields = resolveDisplayFields(savedView.display_fields)

  const savedViewFetchConfig = useMemo(
    () => buildSavedViewFetchConfig(savedView),
    [savedView.id, savedView.page_size, savedView.sort_field, savedView.sort_reverse, savedView.filter_rules]
  )
  const savedViewQueryKey = useMemo(() => JSON.stringify(savedViewFetchConfig), [savedViewFetchConfig])
  const loadedQueryKeyRef = useRef<string | null>(null)

  const reload = useCallback(async () => {
    const isFirstLoadForKey = loadedQueryKeyRef.current !== savedViewQueryKey
    if (isFirstLoadForKey) {
      setLoading(true)
    }

    try {
      const result = await documentService.listFiltered(
        1,
        savedViewFetchConfig.page_size,
        savedViewFetchConfig.sort_field,
        savedViewFetchConfig.sort_reverse,
        savedViewFetchConfig.filter_rules,
        { truncate_content: true }
      )

      setShow(true)
      setDocuments(result.results)
      setCount(result.count)
      loadedQueryKeyRef.current = savedViewQueryKey

      setTimeout(() => {
        setLoading(false)
      }, 500)
    } catch (error) {
      setLoading(false)
    }
  }, [savedViewFetchConfig, savedViewQueryKey])

  useEffect(() => {
    reload()
  }, [reload])

  useEffect(() => {
    if (usersCacheLoaded) return
    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)) {
      setUsersCache([])
      setUsersCacheLoaded(true)
      return
    }
    userService
      .listAll()
      .then((response) => {
        setUsersCache((response.results || []) as DocumentCardUsersCacheEntry[])
        setUsersCacheLoaded(true)
      })
      .catch(() => {
        setUsersCacheLoaded(true)
      })
  }, [usersCacheLoaded])

  useEffect(() => {
    if (displayMode !== DisplayMode.TABLE) return
    customFieldService
      .listAll()
      .then((response) => setCustomFields(response.results || []))
      .catch(() => {})
  }, [displayMode])

  const getCorrespondentName = (document: Document): string => {
    const correspondent = document.correspondent
    if (!correspondent) return ''

    if (typeof correspondent === 'object' && correspondent.name) {
      return correspondent.name
    }

    if (document.correspondent_display?.name) {
      return document.correspondent_display.name
    }

    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Correspondent)) {
      return t('private')
    }

    const correspondentId = typeof correspondent === 'number' ? correspondent : null
    if (!correspondentId) return ''

    if (!correspondentsCacheLoaded || correspondentsCache.length === 0) {
      return ''
    }

    const corr = correspondentsCache.find((c) => c.id === correspondentId)
    return corr?.name || t('private')
  }

  const getDocumentTypeName = (document: Document): string => {
    const documentType = document.document_type
    if (!documentType) return ''

    if (typeof documentType === 'object' && documentType.name) {
      return documentType.name
    }

    if (document.document_type_display?.name) {
      return document.document_type_display.name
    }

    const documentTypeId = typeof documentType === 'number' ? documentType : null
    if (!documentTypeId) return ''

    if (!documentTypesCacheLoaded || documentTypesCache.length === 0) {
      return ''
    }

    const dt = documentTypesCache.find((d) => d.id === documentTypeId)
    return dt?.name || ''
  }

  const getStoragePathName = (document: Document): string => {
    const storagePath = document.storage_path
    if (!storagePath) return ''

    if (typeof storagePath === 'object' && storagePath.name) {
      return storagePath.name
    }

    if (document.storage_path_display?.name) {
      return document.storage_path_display.name
    }

    const storagePathId = typeof storagePath === 'number' ? storagePath : null
    if (!storagePathId) return ''

    if (!storagePathsCacheLoaded || storagePathsCache.length === 0) {
      return ''
    }

    const sp = storagePathsCache.find((s) => s.id === storagePathId)
    return sp?.name || ''
  }

  const getWorkflowStatusName = (workflowStatus: number | { id: number; name?: string } | null | undefined): string => {
    if (!workflowStatus) return ''
    if (typeof workflowStatus === 'object' && workflowStatus.name) return workflowStatus.name
    const id = typeof workflowStatus === 'number' ? workflowStatus : null
    if (!id || !workflowStatusesCacheLoaded) return ''
    const ws = workflowStatusesCache.find((s) => s.id === id)
    return ws?.name ?? ''
  }

  const getDisplayCustomFieldTitle = (fieldId: string): string => {
    const fieldIdNum = parseInt(fieldId.replace(DisplayFieldEnum.CUSTOM_FIELD, ''), 10)
    const field = customFields.find((f) => f.id === fieldIdNum)
    return field ? field.name : fieldId
  }

  const getColumnTitle = (field: DisplayFieldEnum): string => {
    if (typeof field === 'string' && field.startsWith(DisplayFieldEnum.CUSTOM_FIELD) && field.length > DisplayFieldEnum.CUSTOM_FIELD.length) {
      return getDisplayCustomFieldTitle(field)
    }

    const labelMap: Partial<Record<DisplayFieldEnum, string>> = {
      [DisplayFieldEnum.ASN]: t('asn'),
      [DisplayFieldEnum.CORRESPONDENT]: t('correspondent'),
      [DisplayFieldEnum.TITLE]: t('title'),
      [DisplayFieldEnum.TAGS]: t('tags'),
      [DisplayFieldEnum.OWNER]: t('owner'),
      [DisplayFieldEnum.NOTES]: t('notes'),
      [DisplayFieldEnum.DOCUMENT_TYPE]: t('document_type'),
      [DisplayFieldEnum.WORKFLOW_STATUS]: t('document_workflow.status'),
      [DisplayFieldEnum.STORAGE_PATH]: t('storage_path'),
      [DisplayFieldEnum.CREATED]: t('created'),
      [DisplayFieldEnum.ADDED]: t('added'),
      [DisplayFieldEnum.PAGE_COUNT]: t('pages'),
      [DisplayFieldEnum.SHARED]: t('shared'),
      [DisplayFieldEnum.CUSTOM_FIELD]: t('custom_fields'),
    }
    return labelMap[field] || field
  }

  const showAll = () => {
    if (savedView.show_in_sidebar) {
      navigate(`/view/${savedView.id}`)
    } else {
      navigate(`/documents?view=${savedView.id}`)
    }
  }

  const clickTag = (_tagId: number, event?: React.MouseEvent) => {
    event?.preventDefault()
    event?.stopPropagation()
  }

  const clickCorrespondent = (_correspondentId: number, event?: React.MouseEvent) => {
    event?.preventDefault()
    event?.stopPropagation()
  }

  const clickDocumentType = (_docTypeId: number, event?: React.MouseEvent) => {
    event?.preventDefault()
    event?.stopPropagation()
  }

  const clickStoragePath = (_storagePathId: number, event?: React.MouseEvent) => {
    event?.preventDefault()
    event?.stopPropagation()
  }

  const clickMoreLike = (_documentId: number) => {}

  const openDocumentDetail = (document: Document) => {
    navigate(`/documents/${document.id}/details`)
  }

  const toggleSelected = (_document: Document, _event: React.MouseEvent) => {}

  const headerButtons =
    documents.length > 0 ? (
      <button className="btn btn-link text-decoration-none" onClick={showAll}>
        {t('show_all')}
      </button>
    ) : null

  return (
    <div ref={setNodeRef} style={style}>
      <WidgetFrame
        title={savedView.name}
        badge={count}
        loading={loading}
        headerButtons={headerButtons}
        dragHandleProps={{ ...attributes, ...listeners }}
      >
        <div className={`wrapper fade ${show ? 'show' : ''}`}>
          {displayMode === DisplayMode.TABLE ? (
            documents.length === 0 && !loading ? (
              <p className="text-center text-muted mb-0 fst-italic">{t('no_documents')}</p>
            ) : (
              <div className="saved-view-table-wrapper table-responsive">
                <table className="table table-hover mb-0 mt-n2 align-middle">
                <thead>
                  <tr>
                    {displayFields.map((field) => (
                      <th
                        key={field}
                        scope="col"
                        className={field === DisplayFieldEnum.CREATED || field === DisplayFieldEnum.ADDED ? 'w-25' : ''}
                      >
                        {getColumnTitle(field)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, i) => (
                    <tr key={doc.id}>
                      {displayFields.map((field, j) => (
                        <td key={`${doc.id}-${field}`} className="py-2 py-md-3 position-relative">
                          {loading && show ? (
                            <div className="placeholder-glow text-start">
                              <span className="placeholder bg-secondary w-50" style={{ opacity: 1 - (i * 1) / documents.length }} />
                            </div>
                          ) : (
                            <>
                              <TableCell
                                field={field}
                                document={doc}
                                customFields={customFields}
                                usersCache={usersCache}
                                usersCacheLoaded={usersCacheLoaded}
                                getCorrespondentName={getCorrespondentName}
                                getDocumentTypeName={getDocumentTypeName}
                                getStoragePathName={getStoragePathName}
                                getWorkflowStatusName={getWorkflowStatusName}
                                clickCorrespondent={clickCorrespondent}
                                clickDocumentType={clickDocumentType}
                                clickStoragePath={clickStoragePath}
                                clickTag={clickTag}
                              />
                              {j === displayFields.length - 1 && (
                                <div
                                  className="btn-group position-absolute top-50 end-0 translate-middle-y saved-view-row-actions"
                                  onMouseLeave={() => {
                                    const previewRef = previewRefs.current.get(doc.id)
                                    if (previewRef) {
                                      previewRef.close()
                                    }
                                  }}
                                >
                                  <PreviewPopup
                                    document={doc}
                                    linkClasses="btn px-4 btn-dark border-dark-subtle"
                                    ref={(ref) => {
                                      if (ref) {
                                        previewRefs.current.set(doc.id, ref)
                                      } else {
                                        previewRefs.current.delete(doc.id)
                                      }
                                    }}
                                  >
                                    <i className="bi bi-eye"></i>
                                  </PreviewPopup>
                                  <a
                                    href={documentService.getDownloadUrl(doc.id)}
                                    className="btn px-4 btn-dark border-dark-subtle"
                                    title={t('download')}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <i className="bi bi-download"></i>
                                  </a>
                                </div>
                              )}
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )
          ) : displayMode === DisplayMode.SMALL_CARDS ? (
            <div className="row row-cols-docdot-cards my-n2">
              {documents.map((d) => (
                <DocumentCardSmall
                  key={d.id}
                  document={d}
                  displayFields={displayFields.map((f) => f.toString())}
                  usersCache={usersCache}
                  usersCacheLoaded={usersCacheLoaded}
                  onToggleSelected={toggleSelected}
                  onDoubleClickDocument={openDocumentDetail}
                  onClickTag={clickTag}
                  onClickCorrespondent={clickCorrespondent}
                  onClickDocumentType={clickDocumentType}
                  onClickStoragePath={clickStoragePath}
                  selected={false}
                />
              ))}
            </div>
          ) : displayMode === DisplayMode.LARGE_CARDS ? (
            <div className="saved-view-large-cards-wrapper">
              <div className="row my-n2">
                {documents.map((d) => (
                  <DocumentCardLarge
                    key={d.id}
                    document={d}
                    displayFields={displayFields}
                    usersCache={usersCache}
                    usersCacheLoaded={usersCacheLoaded}
                    onToggleSelected={toggleSelected}
                    onDoubleClickDocument={openDocumentDetail}
                    onClickTag={clickTag}
                    onClickCorrespondent={clickCorrespondent}
                    onClickDocumentType={clickDocumentType}
                    onClickStoragePath={clickStoragePath}
                    onClickMoreLike={clickMoreLike}
                    selected={false}
                  />
                ))}
              </div>
            </div>
          ) : displayMode === DisplayMode.HIERARCHY ? (
            <div className="saved-view-hierarchy-wrapper">
              <DocumentHierarchyView
                documents={documents}
                displayFields={displayFields}
                selected={new Set<number>()}
                onToggleSelected={toggleSelected}
                onDoubleClickDocument={openDocumentDetail}
              />
            </div>
          ) : documents.length === 0 ? (
            <p className="text-center text-muted mb-0 fst-italic">{t('no_documents')}</p>
          ) : null}
        </div>
      </WidgetFrame>
    </div>
  )
}

interface TableCellProps {
  field: DisplayFieldEnum
  document: Document
  customFields: CustomField[]
  usersCache: DocumentCardUsersCacheEntry[]
  usersCacheLoaded: boolean
  getCorrespondentName: (document: Document) => string
  getDocumentTypeName: (document: Document) => string
  getStoragePathName: (document: Document) => string
  getWorkflowStatusName: (workflowStatus: number | { id: number; name?: string } | null | undefined) => string
  clickCorrespondent: (id: number, event?: React.MouseEvent) => void
  clickDocumentType: (id: number, event?: React.MouseEvent) => void
  clickStoragePath: (id: number, event?: React.MouseEvent) => void
  clickTag: (id: number, event?: React.MouseEvent) => void
}

const TableCell: React.FC<TableCellProps> = ({
  field,
  document,
  customFields,
  usersCache,
  usersCacheLoaded,
  getCorrespondentName,
  getDocumentTypeName,
  getStoragePathName,
  getWorkflowStatusName,
  clickCorrespondent,
  clickDocumentType,
  clickStoragePath,
  clickTag,
}) => {
  const { t } = useTranslation()

  if (typeof field === 'string' && field.startsWith(DisplayFieldEnum.CUSTOM_FIELD) && field.length > DisplayFieldEnum.CUSTOM_FIELD.length) {
    const fieldIdNum = parseInt(field.replace(DisplayFieldEnum.CUSTOM_FIELD, ''), 10)
    const customFieldInstance = document.custom_fields?.find((cf) => cf.field === fieldIdNum)
    const fieldDef = customFields.find((f) => f.id === fieldIdNum)
    const displayValue = customFieldInstance ? formatCustomFieldDisplayValue(customFieldInstance, fieldDef) : ''
    return displayValue ? <span>{displayValue}</span> : <span>-</span>
  }

  switch (field) {
    case DisplayFieldEnum.ADDED:
      return (
        <Link to={`/documents/${document.id}`} className="btn-link text-dark text-decoration-none py-2 py-md-3">
          {formatDate(document.added)}
        </Link>
      )
    case DisplayFieldEnum.CREATED:
      return (
        <Link to={`/documents/${document.id}`} className="btn-link text-dark text-decoration-none py-2 py-md-3">
          {formatDate(document.created)}
        </Link>
      )
    case DisplayFieldEnum.TITLE:
      return (
        <Link to={`/documents/${document.id}`} className="btn-link text-dark text-decoration-none py-2 py-md-3">
          {document.title}
        </Link>
      )
    case DisplayFieldEnum.CORRESPONDENT: {
      if (!document.correspondent) return null
      const correspondent = document.correspondent
      const correspondentId = typeof correspondent === 'object' ? correspondent.id : correspondent
      return (
        <button className="btn btn-link text-dark text-decoration-none p-0" onClick={(e) => clickCorrespondent(correspondentId, e)}>
          {getCorrespondentName(document)}
        </button>
      )
    }
    case DisplayFieldEnum.DOCUMENT_TYPE: {
      if (!document.document_type) return null
      const documentType = document.document_type
      const documentTypeId = typeof documentType === 'object' ? documentType.id : documentType
      return (
        <button className="btn btn-link text-dark text-decoration-none p-0" onClick={(e) => clickDocumentType(documentTypeId, e)}>
          {getDocumentTypeName(document)}
        </button>
      )
    }
    case DisplayFieldEnum.STORAGE_PATH: {
      if (!document.storage_path) return null
      const storagePath = document.storage_path
      const storagePathId = typeof storagePath === 'object' ? storagePath.id : storagePath
      return (
        <button className="btn btn-link text-dark text-decoration-none p-0" onClick={(e) => clickStoragePath(storagePathId, e)}>
          {getStoragePathName(document)}
        </button>
      )
    }
    case DisplayFieldEnum.TAGS:
      return document.tags && document.tags.length > 0 ? (
        <div className="d-flex flex-wrap gap-1">
          {document.tags.map((tagId) => {
            const found = (document.tags_display || []).find((tag) => tag.id === tagId)
            const tagDisplay: TagType | undefined = found
              ? {
                  id: found.id,
                  name: found.name,
                  color: found.color || '#6c757d',
                  text_color: found.text_color || '#ffffff',
                  parent: found.parent ?? null,
                  is_inbox_tag: false,
                  matching_algorithm: 1,
                  match: '',
                  is_insensitive: true,
                }
              : undefined
            return (
              <Tag
                key={tagId}
                tag={tagDisplay}
                tagID={tagId}
                clickable={true}
                linkTitle={t('filter_by_tag')}
                onClick={(id) => clickTag(id)}
              />
            )
          })}
        </div>
      ) : null
    case DisplayFieldEnum.ASN:
      return <>{document.archive_serial_number}</>
    case DisplayFieldEnum.PAGE_COUNT:
      return <>{document.page_count}</>
    case DisplayFieldEnum.SHARED:
      return <>{document.is_shared_by_requester ? t('yes') : t('no')}</>
    case DisplayFieldEnum.OWNER:
      return <>{getDocumentOwnerDisplayName(document, usersCache, usersCacheLoaded)}</>
    case DisplayFieldEnum.WORKFLOW_STATUS:
      return document.workflow_status ? (
        <span
          className="badge"
          style={{
            backgroundColor: (document.workflow_status as { color?: string })?.color || '#6c757d',
            color: (document.workflow_status as { text_color?: string })?.text_color || '#fff',
          }}
        >
          {getWorkflowStatusName(document.workflow_status)}
        </span>
      ) : null
    case DisplayFieldEnum.NOTES:
      return document.notes?.length ? (
        <Link to={`/documents/${document.id}/notes`} className="btn btn-sm p-0">
          <span className="badge rounded-pill bg-light border text-primary">
            <i className="bi bi-chat-left-text me-1"></i>
            {document.notes.length}
          </span>
        </Link>
      ) : null
    default:
      return null
  }
}
